import { router } from "@inertiajs/react"
import type {
  Page as InertiaPage,
  PageProps,
  RequestPayload,
} from "@inertiajs/core"

import {
  ANONYMOUS_SESSION,
  createLeaveGuard,
  type Entry,
  type Flash,
  type Host,
  type Readable,
  type Session,
  type Visit,
  type VisitRequest,
} from "./host"
import type { Page, SerialisableBody, TransientData } from "./types"

// The root host over Inertia: the one layer this document runs, over
// Inertia's router and session history. It is the only place a page's
// runtime touches Inertia; stage 3 replaces it with the layer runtime and
// no page changes.
//
// Inertia renders the page through its App, which is also where the current
// page is read (`usePage`). The binding hands each page it sees to `sync`,
// and the router's events notify readers of every commit that follows.

// What Inertia shares on every page, as the Rails side names it.
type SharedProps = {
  authenticated: boolean
  user: Session["user"]
  workspaces: Session["workspaces"]
  impersonating: boolean
  feature_toggles: string[]
  flash: Flash
  transient: TransientData
  path_params: Record<string, string>
  errors?: Record<string, string>
}

const sharedOf = (page: InertiaPage) => page.props as unknown as SharedProps

function sessionOf(page: InertiaPage): Session {
  const props = sharedOf(page)
  return {
    authenticated: props.authenticated,
    user: props.user,
    workspaces: props.workspaces ?? [],
    impersonating: props.impersonating ?? false,
    featureToggles: props.feature_toggles ?? [],
  } as Session
}

const sameSession = (a: Session, b: Session) =>
  a.authenticated === b.authenticated &&
  a.user === b.user &&
  a.workspaces === b.workspaces &&
  a.impersonating === b.impersonating &&
  a.featureToggles === b.featureToggles

function entryOf(page: InertiaPage): Entry {
  return {
    name: page.component,
    url: page.url,
    pathParams: sharedOf(page).path_params ?? {},
  }
}

function visitOf(page: InertiaPage, pending: boolean): Visit {
  const props = sharedOf(page)
  return {
    pending,
    props: page.props as unknown as SerialisableBody,
    flash: props.flash ?? {},
    transient: props.transient ?? {},
    errors: props.errors ?? {},
  }
}

// The page Inertia last committed, and what the readables derived from it.
// `sync` stages the page the App is rendering; the router's `navigate`
// event confirms it and notifies readers.
let currentPage: InertiaPage | null = null
let pending = false
let snapshot: {
  page: InertiaPage | null
  session: Session
  entry: Entry
  visit: Visit
} = {
  page: null,
  session: ANONYMOUS_SESSION,
  entry: { name: "", url: "", pathParams: {} },
  visit: { pending, props: {}, flash: {}, transient: {}, errors: {} },
}

function current() {
  if (currentPage !== null && snapshot.page !== currentPage) {
    const session = sessionOf(currentPage)
    snapshot = {
      page: currentPage,
      session: sameSession(snapshot.session, session)
        ? snapshot.session
        : session,
      entry: entryOf(currentPage),
      visit: visitOf(currentPage, pending),
    }
  }
  if (snapshot.visit.pending !== pending) {
    snapshot = { ...snapshot, visit: { ...snapshot.visit, pending } }
  }
  return snapshot
}

const listeners = new Set<() => void>()
let listening = false

function notify() {
  for (const listener of [...listeners]) listener()
}

// The router is a singleton, so its events are listened to once, from the
// first subscription on.
function listen() {
  if (listening) return
  listening = true
  router.on("navigate", (event) => {
    currentPage = event.detail.page
    notify()
  })
  router.on("start", () => {
    pending = true
    notify()
  })
  router.on("finish", () => {
    pending = false
    notify()
  })
}

function subscribe(onChange: () => void) {
  listen()
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
  }
}

const readable = <T>(select: () => T): Readable<T> => ({
  get: select,
  subscribe,
})

const sameEntry = (page: InertiaPage) =>
  currentPage !== null &&
  page.component === currentPage.component &&
  page.url === currentPage.url

// A request's outcome keeps the page mounted when it lands on the same entry
// (a mutation answered in place, a reload) and remounts it when it lands on
// another; `replace` and `preserveUrl` say the entry is being updated.
function navigate({
  href,
  method = "get",
  data = {},
  replace = false,
  preserveScroll = false,
  preserveUrl = false,
  formData = false,
}: VisitRequest): Promise<Visit> {
  return new Promise<Visit>((resolve) => {
    router.visit(href, {
      method,
      data: data as RequestPayload,
      replace,
      preserveScroll,
      preserveUrl,
      forceFormData: formData,
      preserveState:
        method !== "get" || replace || preserveUrl ? true : sameEntry,
      onSuccess: (page) => resolve(visitOf(page, false)),
      // Inertia has committed the page carrying the errors by the time it
      // reports them, so the visit resolved is that page's.
      onError: (errors) => resolve({ ...current().visit, errors }),
    })
  })
}

// A page a child layer delivered: committed without a request, as the
// child's fetch already followed any redirect and consumed its flash.
// Landing on the page the child was opened from replaces the history entry,
// so Back still leaves the page; landing anywhere else pushes one. Both
// preserve state and scroll: Inertia remounts the page component under a
// new key otherwise, which would drop the host page's local state, its
// scroll position, and the trigger that focus returns to.
function receive(page: Page) {
  const { pathname, search, hash } = page.baseUrl
  const visit = {
    url: `${pathname}${search}${hash}`,
    component: page.name,
    props: page.props as PageProps,
    preserveState: true,
    preserveScroll: true,
  }

  if (pathname === window.location.pathname) {
    router.replace(visit)
  } else {
    router.push(visit)
  }
}

const host: Host = {
  session: readable(() => current().session),
  entry: readable(() => current().entry),
  visit: readable(() => current().visit),
  navigate,
  guard: createLeaveGuard(),
  receive,
}

// The host, with the page Inertia's App is rendering staged for the readers
// rendering in the same pass. Notification follows from the router's own
// `navigate` event once the commit lands.
export function inertiaHost(page: InertiaPage): Host {
  if (page !== currentPage) currentPage = page
  return host
}
